import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * Abas do núcleo (r6 visual M2), no padrão de abas WAI-ARIA com ativação automática:
 *  - roving tabindex: só a aba selecionada entra na ordem de Tab; as setas esquerda e direita (com volta), Home e End
 *    movem a seleção e o foco juntos; `aria-selected` marca a selecionada.
 *  - Aba que navega (`para`): o foco é pedido antes da navegação e entregue quando a aba nova aparece selecionada, mesmo que
 *    a tela tenha sido remontada no caminho (troca de módulo). Enter e clique também pedem o foco.
 *  - Estado selecionado com indicador de 3:1 além da cor do texto (r6 visual B1): sublinhado ou borda de 2px em `acento-forte`.
 * Use sempre este componente para qualquer conjunto de abas; `PainelAba` liga o conteúdo à aba (`aria-controls`).
 */

export type TeclaAba = 'ArrowRight' | 'ArrowLeft' | 'Home' | 'End';

/** Índice da aba de destino para a tecla, ou -1 quando a tecla não move (função pura, testada em `tests/abas-teclado.test.ts`). */
export function destinoDaTecla(atual: number, total: number, tecla: string): number {
  if (total <= 0) return -1;
  const i = atual < 0 ? 0 : atual;
  if (tecla === 'ArrowRight') return (i + 1) % total;
  if (tecla === 'ArrowLeft') return (i - 1 + total) % total;
  if (tecla === 'Home') return 0;
  if (tecla === 'End') return total - 1;
  return -1;
}

export interface ItemAba<K extends string = string> {
  chave: K;
  rotulo: ReactNode;
  /** Aba que navega: caminho do link. */
  para?: string;
  /** D2: ícone antes do rótulo (o glifo do menu lateral, `size-4`, cor do texto); sem ele, só o texto, como sempre. */
  icone?: ReactNode;
}

type Variante = 'segmentado' | 'chips' | 'compacto';

const CONTAINER: Record<Variante, string> = {
  segmentado: 'flex w-fit max-w-full gap-1 overflow-x-auto rounded-lg border border-borda p-1',
  chips: 'flex flex-wrap gap-2',
  compacto: 'flex gap-1',
};

const ABA: Record<Variante, { base: string; sim: string; nao: string }> = {
  segmentado: { base: 'min-h-11 rounded-md border-b-2 px-3 py-1.5 text-sm font-medium whitespace-nowrap md:min-h-0', sim: 'border-acento-forte bg-sup-3 text-tinta-1', nao: 'border-transparent text-tinta-3 hover:text-tinta-1' },
  chips: { base: 'min-h-11 rounded-lg border-2 px-3 py-1.5 text-sm font-medium md:min-h-0', sim: 'border-acento-forte bg-acento-suave text-acento-forte', nao: 'border-borda text-tinta-2 hover:text-tinta-1' },
  compacto: { base: 'rounded-md border-b-2 px-2.5 py-1 text-xs font-medium', sim: 'border-acento-forte bg-acento-suave text-acento-forte', nao: 'border-transparent text-tinta-3 hover:text-tinta-1' },
};

/** Pedido de foco que sobrevive à remontagem da tela (aba que navega): grupo e chave da aba que deve receber o foco. */
let focoPendente: { grupo: string; chave: string; ate: number } | null = null;

export const idAba = (idBase: string, chave: string) => `${idBase}-aba-${chave}`;
export const idPainel = (idBase: string, chave: string) => `${idBase}-painel-${chave}`;

export function Abas<K extends string>({
  itens,
  ativa,
  aoMudar,
  rotulo,
  idBase,
  variante = 'segmentado',
  fundo = 'bg-sup-1',
  className = '',
  grupo,
}: {
  itens: ItemAba<K>[];
  ativa: K;
  /** Abas locais: troca a aba. Abas que navegam (`para`): opcional, chamado depois de navegar. */
  aoMudar?: (chave: K) => void;
  rotulo: string;
  /** Liga as abas aos painéis (`PainelAba`) por `aria-controls`. */
  idBase?: string;
  variante?: Variante;
  /** Fundo do contêiner segmentado (bg-sup-1 ou bg-sup-2). */
  fundo?: string;
  className?: string;
  /** Nome estável do conjunto (data-abas), usado para devolver o foco depois de uma navegação. */
  grupo?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const nomeGrupo = grupo ?? idBase ?? rotulo;

  // Foco pedido por uma navegação: chega quando a aba pedida aparece selecionada (inclusive depois de remontar).
  useEffect(() => {
    const p = focoPendente;
    if (!p || p.grupo !== nomeGrupo || p.chave !== ativa) return;
    focoPendente = null;
    if (Date.now() > p.ate) return;
    const alvo = ref.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    alvo?.focus();
  }, [ativa, nomeGrupo]);

  function selecionar(i: number, focar: boolean) {
    const item = itens[i];
    if (!item) return;
    if (item.para) {
      focoPendente = focar ? { grupo: nomeGrupo, chave: item.chave, ate: Date.now() + 5000 } : null;
      ref.current?.querySelectorAll<HTMLAnchorElement>('[role="tab"]')[i]?.click();
      return;
    }
    aoMudar?.(item.chave);
    if (focar) requestAnimationFrame(() => ref.current?.querySelectorAll<HTMLElement>('[role="tab"]')[i]?.focus());
  }

  function aoTeclar(e: KeyboardEvent<HTMLDivElement>) {
    const atual = itens.findIndex((x) => x.chave === ativa);
    const vai = destinoDaTecla(atual, itens.length, e.key);
    if (vai < 0) return;
    e.preventDefault();
    if (vai === atual) {
      ref.current?.querySelectorAll<HTMLElement>('[role="tab"]')[vai]?.focus();
      return;
    }
    selecionar(vai, true);
  }

  const estilo = ABA[variante];
  return (
    <div ref={ref} role="tablist" aria-label={rotulo} data-abas={nomeGrupo} onKeyDown={aoTeclar} className={`${CONTAINER[variante]} ${variante === 'segmentado' ? fundo : ''} ${className}`}>
      {itens.map((item) => {
        const sel = item.chave === ativa;
        const comum = {
          role: 'tab' as const,
          'aria-selected': sel,
          'aria-controls': idBase && !item.para ? idPainel(idBase, item.chave) : undefined,
          id: idBase ? idAba(idBase, item.chave) : undefined,
          tabIndex: sel ? 0 : -1,
          className: `${estilo.base} ${sel ? estilo.sim : estilo.nao}${item.icone ? ' inline-flex items-center gap-1.5' : ''}`,
        };
        if (item.para) {
          return (
            <Link
              key={item.chave}
              {...comum}
              to={item.para}
              onClick={() => {
                // Clique ou Enter: a aba nova recebe o foco quando aparecer (a tela pode remontar ao trocar de módulo).
                if (!focoPendente || focoPendente.chave !== item.chave) focoPendente = { grupo: nomeGrupo, chave: item.chave, ate: Date.now() + 5000 };
                aoMudar?.(item.chave);
              }}
            >
              {item.icone}
              {item.rotulo}
            </Link>
          );
        }
        return (
          <button key={item.chave} type="button" {...comum} onClick={() => aoMudar?.(item.chave)}>
            {item.icone}
            {item.rotulo}
          </button>
        );
      })}
    </div>
  );
}

/** Painel da aba local, ligado a ela por `aria-labelledby` (só o da aba ativa é montado). */
export function PainelAba({ idBase, chave, className, children }: { idBase: string; chave: string; className?: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={idPainel(idBase, chave)} aria-labelledby={idAba(idBase, chave)} className={className}>
      {children}
    </div>
  );
}
